import IcNewCustomer from 'assets/Icons/IcNewCustomer';
import IcRegularCustomer from 'assets/Icons/IcRegularCustomer';
import IcTickV3 from 'assets/Icons/IcTickV3';
import Box from 'components/Box';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import React, { useMemo } from 'react';
import { CUSTOMER_TARGET_TYPE } from 'services/shop/constants';
import { ICustomerItemData } from 'services/shop/types/customer';
import styled from 'styled-components';
import { maskPhone } from 'utils/formatPhone';

const bgColors = [
  '#808836',
  '#9579DA',
  '#D56FAC',
  '#0DA472',
  '#3AADD9',
  '#FF7D00',
  '#F4C04B',
  '#F5767F',
];

type ICustomerItemProps = {
  data: ICustomerItemData;
  type?: CUSTOMER_TARGET_TYPE;
  selected?: boolean;
  disabledPoint?: boolean;
  onClick?: () => void;
};
const CustomerItem: React.FC<ICustomerItemProps> = ({
  selected,
  data,
  type = CUSTOMER_TARGET_TYPE.NORMAL,
  disabledPoint,
  onClick,
}) => {
  const accountPackageConfig =
    ACCOUNT_PACKAGE_CONFIG[data.accountPackage as ACCOUNT_PACKAGE];

  const randomBgColor = useMemo(
    () => bgColors[Math.floor(Math.random() * bgColors.length)],
    [],
  );
  return (
    <CustomerItemStyled
      className={`${type} ${selected ? 'selected' : ''}`}
      onClick={onClick}
    >
      <Box
        width='72px'
        height='72px'
        display='flex'
        alignItems='center'
        justifyContent='center'
        borderRadius='100%'
        style={{ flexShrink: 0, backgroundColor: data?.color ?? randomBgColor }}
      >
        {data.imageAvatarUrl ? (
          <img
            src={data.imageAvatarUrl || '/images/avatar-default.png'}
            alt='avatar'
            style={{ width: '3rem', height: '3rem', borderRadius: '50%' }}
          />
        ) : (
          <Text variant='H5' color='text_1' textTransform='uppercase'>
            {data.name?.split(' ').map((n) => n[0]).join('') || 'U'} 
          </Text>
        )}
      </Box>
      <Box
        display='flex'
        flexDirection='column'
        gap='1'
        style={{ flex: 1, minWidth: 0 }}
      >
        <Text className='name' variant='H5' textTransform='uppercase'>
          {data.name || 'UNKNOWN CUSTOMER'}
        </Text>
        <Text variant='H7' color='text_3_v2' style={{ fontWeight: '500' }}>
          {maskPhone(data.phone)}
        </Text>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          style={{ marginTop: '4px' }}
        >
          <BoxIcon style={{ backgroundColor: accountPackageConfig?.bgColor }}>
            {accountPackageConfig?.icon}
            <Text
              variant='H7'
              className='label'
              style={{ color: accountPackageConfig?.color }}
            >
              {accountPackageConfig?.label}
            </Text>
          </BoxIcon>
          {!disabledPoint && (
            <Text variant='H7' color='v2_primary_bg_6' className='point'>
              {data.point || 0} point
            </Text>
          )}
        </Box>
      </Box>
      {selected && (
        <div className='selected'>
          <IcTickV3 fill={colorTheme.text_1} />
        </div>
      )}
    </CustomerItemStyled>
  );
};

export default CustomerItem;
type CustomerItemStyledProps = {};
const CustomerItemStyled = styled.button<CustomerItemStyledProps>`
  width: 100%;
  min-width: 0;
  border-radius: 12px;
  flex-shrink: 0;
  padding: 20px;
  text-align: left;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1rem;
  position: relative;
  font-family: Poppins;

  .selected {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 99;
    transform: translate(15px, -15px);
    background: ${colorTheme.v2_secondary_3};
    border-radius: 100%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .box-name {
    width: 100%;
  }
  .name {
    min-width: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  background: #fff;
  border: 1px solid ${colorTheme.v2_border_2};
  box-shadow: 0 0 20px 0 rgba(95, 186, 203, 0.15);

  .point {
    font-weight: 500;
    font-family: Poppins;
  }

  &.selected {
    border: 1px solid ${colorTheme.v2_secondary_3};
    background: ${colorTheme.v2_secondary_1};
  }
`;

const BoxIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 100px;

  .label {
    font-family: Poppins;
  }
`;

enum ACCOUNT_PACKAGE {
  REGULAR = 'REGULAR',
  NEW = 'NEW',
}

const ACCOUNT_PACKAGE_CONFIG = {
  [ACCOUNT_PACKAGE.REGULAR]: {
    label: 'Regular',
    icon: <IcRegularCustomer width={28} height={28} />,
    color: '#2D6772',
    bgColor: '#F2FEFF',
  },
  [ACCOUNT_PACKAGE.NEW]: {
    label: 'New',
    icon: <IcNewCustomer width={28} height={28} />,
    color: '#A95A23',
    bgColor: '#FDF4CE',
  },
};
